import React, { useState, useEffect, useCallback } from 'react';
import wishlistService from '../services/wishlistService';
import userService from '../services/userService';
import slugService from '../services/slugService';
import InsertSetCard from '../components/InsertSetCard';
import PageGrid from '../components/PageGrid';
import Sidebar from '../components/Sidebar';
import SidebarSection from '../components/Sidebar/SidebarSection';
import SidebarRow from '../components/Sidebar/SidebarRow';
import SidebarImage from '../components/Sidebar/SidebarImage';
import UserContactCard from '../components/UserContactCard';
import UserProfileLink from '../components/UserProfileLink';
import usePageTitle from '../utils/usePageTitle';
import { getUserWishlistProductBreadcrumbs } from '../utils/breadcrumbUtils';
import { filterInsertSets } from '../utils/filters';
import './ProductPage.css';

const UserWishlistProductPage = ({ username, productSlug }) => {
  const [product, setProduct] = useState(null);
  const [userData, setUserData] = useState(null);

  // Fetch product and user data on initial load
  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch user data first
        if (username) {
          const user = await userService.getUserByUsername(username);
          setUserData(user);
        }

        // Fetch product data
        if (productSlug) {
          const productData = await slugService.getProductBySlug(productSlug);
          setProduct(productData);
        }
      } catch (error) {
        console.error('Error fetching initial data:', error);
      }
    };
    
    fetchData();
  }, [username, productSlug]);
  
  // Function to fetch insert sets for a specific page
  const fetchInsertSets = useCallback(async (page, limit) => {
    if (!product || !userData) {
      return {
        items: [],
        currentPage: 1,
        totalPages: 1,
        totalItems: 0
      };
    }
    
    try {
      const response = await wishlistService.getUserWishlistInsertSetsByProduct(userData.id, product.id, page, limit);
      
      return {
        items: response.insertSets,
        currentPage: response.currentPage,
        totalPages: response.totalPages,
        totalItems: response.totalInsertSets
      };
    } catch (error) {
      console.error('Error fetching user wishlist insert sets:', error);
      throw error;
    }
  }, [product, userData]);
  
  
  // Function to render each insert set card
  const renderInsertSetCard = useCallback((insertSet, index) => {
    return (
      <InsertSetCard 
        insertSet={insertSet}
        index={index}
        linkUrl={slugService.getUserWishlistInsertSetUrl(insertSet, product, username)}
      />
    );
  }, [product, username]);
  
  // Prepare the title including product info
  const title = product && userData 
    ? `${product.name} (${product.year})` 
    : '';
    
  // Set the page title
  usePageTitle(product && userData ? [`${userData.name}'s Wishlist`, `${product.name} (${product.year})`] : '');
  
  // Render the sidebar content with product details
  const renderSidebar = useCallback(() => {
    if (!product || !userData) return null;
    
    return (
      <Sidebar title={`${userData.name}'s Wishlist`}>
        <SidebarImage
          src={product.imageUrl}
          alt={product.name}
        />
        
        <SidebarSection title="Product Details">
          <SidebarRow
            label="Product"
            value={product.name}
          />
          <SidebarRow
            label="Year"
            value={product.year}
          />
          {product.publisher && (
            <SidebarRow
              label="Publisher"
              value={product.publisher}
            />
          )}
        </SidebarSection>
        
        {/* User Contact Information Card */}
        <UserContactCard user={userData} />
      </Sidebar>
    );
  }, [product, userData]);
  
  if(!product || !userData) {
    return <></>;
  }
  
  // Generate breadcrumb items for user wishlist product page
  const breadcrumbItems = getUserWishlistProductBreadcrumbs(userData, product);

  return (
    <PageGrid
      title={title}
      subtitle={<UserProfileLink user={userData} />}
      fetchItems={fetchInsertSets}
      filterFunction={filterInsertSets}
      renderItem={renderInsertSetCard}
      renderSidebar={renderSidebar}
      placeholderText={`Filter ${userData?.name || ''}'s wishlist insert sets...`}
      emptyStateMessage={`No insert sets found in ${userData?.name || ''}'s wishlist for this product`}
      itemsPerPage={100}
      backButton={{
        url: `/@${username}/wishlist`,
        label: "Back"
      }}
      breadcrumbItems={breadcrumbItems}
    />
  );
};

export default UserWishlistProductPage;